import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  LoginResponse,
  AuthTokens,
  IUser,
  IUserCreate,
  IUserUpdate,
  IImportBatchResult,
  IPost,
  IPostCreate,
  IPostFilters,
  PostPriority,
  INotification,
  IZoneBase,
  ITopicBase,
} from '@barrikadave/shared';
import type { ImportOptions } from '../components/csv-import/types';

// In production the admin is served from the same origin as the API (rrb.ps.al),
// so default to a same-origin /api path. In dev (Vite on :5173) fall back to the
// local API on :3000. VITE_API_URL still overrides both when set.
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.origin}/api`
    : 'http://localhost:3000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

function processQueue(error: Error | null, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const isAuthRequest = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post<ApiResponse<AuthTokens>>(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
        );

        if (data.success && data.data) {
          const { accessToken, refreshToken: newRefreshToken } = data.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          processQueue(null, accessToken);
          return api(originalRequest);
        }

        throw new Error('Refresh failed');
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const { data } = await api.post<ApiResponse<LoginResponse>>('/auth/login', {
        email,
        password,
      });
      if (!data.success || !data.data) {
        throw new Error(data.error || 'Login failed');
      }
      return data.data;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data) {
        const msg = (err.response.data as ApiResponse<unknown>).error;
        throw new Error(msg || 'Email ose fjalekalim i gabuar.');
      }
      throw err;
    }
  },

  refresh: async (refreshToken: string): Promise<AuthTokens> => {
    const { data } = await api.post<ApiResponse<AuthTokens>>('/auth/refresh', {
      refreshToken,
    });
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Refresh failed');
    }
    return data.data;
  },

  me: async (): Promise<IUser> => {
    const { data } = await api.get<ApiResponse<IUser>>('/auth/me');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch user');
    }
    return data.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const { data } = await api.post<ApiResponse<void>>('/auth/me/change-password', {
      currentPassword,
      newPassword,
    });
    if (!data.success) {
      throw new Error(data.error || 'Ndryshimi i fjalekalimit deshtoi');
    }
  },

  setInitialPassword: async (newPassword: string): Promise<void> => {
    const { data } = await api.post<ApiResponse<void>>('/auth/me/set-initial-password', {
      newPassword,
    });
    if (!data.success) {
      throw new Error(data.error || 'Vendosja e fjalekalimit deshtoi');
    }
  },
};

export interface UsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export const usersApi = {
  getAll: async (params?: UsersParams): Promise<PaginatedResponse<IUser>> => {
    const { data } = await api.get<PaginatedResponse<IUser>>('/users', { params });
    return data;
  },

  getById: async (id: string): Promise<IUser> => {
    const { data } = await api.get<ApiResponse<IUser>>(`/users/${id}`);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'User not found');
    }
    return data.data;
  },

  create: async (userData: IUserCreate): Promise<IUser> => {
    const { data } = await api.post<ApiResponse<IUser>>('/users', userData);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to create user');
    }
    return data.data;
  },

  update: async (id: string, userData: IUserUpdate): Promise<IUser> => {
    const { data } = await api.patch<ApiResponse<IUser>>(`/users/${id}`, userData);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to update user');
    }
    return data.data;
  },

  deactivate: async (id: string): Promise<void> => {
    const { data } = await api.patch<ApiResponse<void>>(`/users/${id}/deactivate`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to deactivate user');
    }
  },

  activate: async (id: string): Promise<void> => {
    const { data } = await api.patch<ApiResponse<void>>(`/users/${id}/activate`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to activate user');
    }
  },

  approve: async (id: string): Promise<void> => {
    const { data } = await api.patch<ApiResponse<void>>(`/users/${id}/approve`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to approve user');
    }
  },

  countPending: async (): Promise<{ count: number }> => {
    const { data } = await api.get<ApiResponse<{ count: number }>>('/users/pending/count');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch pending count');
    }
    return data.data;
  },

  resetPassword: async (id: string, newPassword: string, sendEmail?: boolean): Promise<void> => {
    const { data } = await api.post<ApiResponse<void>>(`/users/${id}/reset-password`, {
      newPassword,
      sendEmail,
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to reset password');
    }
  },

  delete: async (id: string): Promise<void> => {
    const { data } = await api.delete<ApiResponse<void>>(`/users/${id}`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete user');
    }
  },

  sendManual: async (id: string): Promise<void> => {
    const { data } = await api.post<ApiResponse<void>>(`/users/${id}/send-manual`);
    if (!data.success) {
      throw new Error(data.error || 'Deshtoi dergimi i manualit');
    }
  },

  importBatch: async (
    users: Array<{
      email: string;
      firstName: string;
      lastName: string;
      password?: string;
      role?: string;
      phone?: string;
      position?: string;
    }>,
    options: ImportOptions,
  ): Promise<IImportBatchResult> => {
    const { data } = await api.post<ApiResponse<IImportBatchResult>>(
      '/users/import',
      { users, options },
      { timeout: 120000 },
    );
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Importimi deshtoi');
    }
    return data.data;
  },
};

export const postsApi = {
  getAll: async (params?: IPostFilters): Promise<PaginatedResponse<IPost>> => {
    const { data } = await api.get<PaginatedResponse<IPost>>('/posts', { params });
    return data;
  },

  getById: async (id: string): Promise<IPost> => {
    const { data } = await api.get<ApiResponse<IPost>>(`/posts/${id}`);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Post not found');
    }
    return data.data;
  },

  create: async (postData: IPostCreate): Promise<IPost> => {
    const { data } = await api.post<ApiResponse<IPost>>('/posts', postData);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to create post');
    }
    return data.data;
  },

  delete: async (id: string): Promise<void> => {
    const { data } = await api.delete<ApiResponse<void>>(`/posts/${id}`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete post');
    }
  },

  update: async (id: string, postData: Partial<IPostCreate>): Promise<IPost> => {
    const { data } = await api.patch<ApiResponse<IPost>>(`/posts/${id}`, postData);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to update post');
    }
    return data.data;
  },
};

export interface DashboardStats {
  totalUsers: number;
  totalPosts: number;
  postsToday: number;
  urgentPosts: number;
  recentPosts: IPost[];
  topAuthors: Array<{
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    postCount: number;
  }>;
}

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get<ApiResponse<DashboardStats>>('/admin/dashboard');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch dashboard stats');
    }
    return data.data;
  },
};

export const categoriesApi = {
  getZones: async (): Promise<IZoneBase[]> => {
    const { data } = await api.get<ApiResponse<IZoneBase[]>>('/categories/zones');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch zones');
    }
    return data.data;
  },

  getTopics: async (): Promise<ITopicBase[]> => {
    const { data } = await api.get<ApiResponse<ITopicBase[]>>('/categories/topics');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch topics');
    }
    return data.data;
  },
};

export interface NotificationHistoryItem {
  id: string;
  type: string;
  title: string;
  body: string;
  postId: string | null;
  deliveryCount: number;
  createdAt: string;
}

export interface NotificationsParams {
  page?: number;
  limit?: number;
  type?: string;
}

export interface SubscriberItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  notificationsEnabled: boolean;
  subscribedSections: string[];
}

export const notificationsApi = {
  getAll: async (
    params?: NotificationsParams,
  ): Promise<PaginatedResponse<NotificationHistoryItem>> => {
    const { data } = await api.get<PaginatedResponse<NotificationHistoryItem>>(
      '/notifications',
      { params },
    );
    return data;
  },

  getSubscribers: async (): Promise<SubscriberItem[]> => {
    const { data } = await api.get<ApiResponse<SubscriberItem[]>>('/notifications/subscribers');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch subscribers');
    }
    return data.data;
  },

  sendToUser: async (
    userId: string,
    title: string,
    body: string,
  ): Promise<{ deliveryCount: number }> => {
    const { data } = await api.post<ApiResponse<{ id: string; deliveryCount: number }>>(
      '/notifications/send-to-user',
      { userId, title, body },
    );
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to send notification');
    }
    return data.data;
  },

  sendUrgent: async (
    title: string,
    body: string,
    postId?: string,
  ): Promise<{ deliveryCount: number }> => {
    const { data } = await api.post<ApiResponse<{ deliveryCount: number }>>(
      '/notifications/send-urgent',
      { title, body, postId: postId || undefined },
    );
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to send notification');
    }
    return data.data;
  },
};

export interface UploadedMedia {
  id: string;
  type: string;
  url: string;
  filename: string;
  mimeType: string;
  size: number;
  thumbnailUrl: string | null;
  order: number;
}

export const mediaApi = {
  upload: async (file: File): Promise<UploadedMedia> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<ApiResponse<UploadedMedia>>('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to upload file');
    }
    return data.data;
  },

  delete: async (id: string): Promise<void> => {
    const { data } = await api.delete<ApiResponse<void>>(`/media/${id}`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete media');
    }
  },
};

// --- App Version Settings ---

export interface AppVersionConfig {
  id: string;
  iosLatestVersion: string;
  iosForceUpdate: boolean;
  iosStoreUrl: string;
  androidLatestVersion: string;
  androidForceUpdate: boolean;
  androidStoreUrl: string;
  updatedAt: string;
}

export const settingsApi = {
  getVersionConfig: async (): Promise<AppVersionConfig> => {
    const { data } = await api.get<ApiResponse<AppVersionConfig>>('/admin/settings/version');
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch version config');
    }
    return data.data;
  },

  updateVersionConfig: async (updates: Partial<AppVersionConfig>): Promise<AppVersionConfig> => {
    const { data } = await api.patch<ApiResponse<AppVersionConfig>>('/admin/settings/version', updates);
    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to update version config');
    }
    return data.data;
  },
};

// --- Activity Logs ---

export interface IActivityLog {
  id: string;
  userId: string;
  action: string;
  // metadata may include `postId` (uuid) and, when resolved server-side,
  // `postTitle` (string) for post-related events.
  metadata?: Record<string, any> | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  deviceInfo?: Record<string, any> | null;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
}

export interface IActivityUserGroup {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  lastEvent: IActivityLog;
  totalEvents: number;
}

export interface ActivityLogParams {
  page?: number;
  limit?: number;
  userId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
}

export interface ActivityUsersGroupedParams {
  page?: number;
  limit?: number;
  action?: string;
  startDate?: string;
  endDate?: string;
}

export interface ActivityStats {
  totalToday: number;
  activeUsersToday: number;
  screenshotAttempts: number;
  clipboardCopies: number;
  topActions: { action: string; count: number }[];
}

export const activityApi = {
  getLogs: async (params?: ActivityLogParams) => {
    const { data } = await api.get<{ success: boolean; data: IActivityLog[]; pagination: any }>(
      '/admin/activity',
      { params },
    );
    if (!data.success) throw new Error('Failed to fetch activity logs');
    return { data: data.data, pagination: data.pagination };
  },

  getUsersGrouped: async (params?: ActivityUsersGroupedParams) => {
    const { data } = await api.get<{
      success: boolean;
      data: IActivityUserGroup[];
      pagination: any;
    }>('/admin/activity/users', { params });
    if (!data.success) throw new Error('Failed to fetch grouped activity');
    return { data: data.data, pagination: data.pagination };
  },

  getStats: async (): Promise<ActivityStats> => {
    const { data } = await api.get<ApiResponse<ActivityStats>>('/admin/activity/stats');
    if (!data.success || !data.data) throw new Error('Failed to fetch activity stats');
    return data.data;
  },

  logAction: async (action: string, metadata?: Record<string, any>): Promise<void> => {
    await api.post('/activity', { action, metadata });
  },
};

export default api;
